import { auth } from '@/firebase/init';
import { applyActionCode } from 'firebase/auth';
import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { AuthFlowNavbar } from '@/components/layouts/Navbar/AuthFlowNavbar';
import { refreshSessionAndReloadApp } from './helpers';
import { NAV_ROUTES } from '@/utils/constants';
import { getHandleInvitationUrlFromSearchParams } from '@/utils/invitationUrlParams';
import { authTremorPrimarySubmitClass } from '../FirebaseAuth/authFormTremor';
import { AuthContactFooter } from '../FirebaseAuth/components/AuthContactFooter';

type ActionStatus = 'loading' | 'success' | 'error' | 'missing_code';

export function VerifyEmailAction() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<ActionStatus>('loading');
  const invitationPath = getHandleInvitationUrlFromSearchParams(searchParams);
  const loginHref = invitationPath
    ? `${NAV_ROUTES.login}?redirectUrl=${encodeURIComponent(invitationPath)}`
    : NAV_ROUTES.login;
  const isActivating = useRef(false);

  useEffect(() => {
    if (isActivating.current) return;
    isActivating.current = true;

    const mode = searchParams.get('mode');
    const oobCode = searchParams.get('oobCode');

    if (mode !== 'verifyEmail' || !oobCode) {
      setStatus('missing_code');
      return;
    }
    const actionCode = oobCode;

    async function completeEmailVerification() {
      try {
        await applyActionCode(auth, actionCode);

        const afterVerify =
          getHandleInvitationUrlFromSearchParams(searchParams) ?? NAV_ROUTES.root;

        if (auth.currentUser) {
          return await refreshSessionAndReloadApp(auth.currentUser, {
            afterNavigateTo: afterVerify,
          });
        }

        setStatus('success');
      } catch (error) {
        console.error('Error finalizing email verification:', error);
        setStatus('error');
      }
    }

    void completeEmailVerification();
  }, [searchParams]);

  return (
    <>
      <AuthFlowNavbar />
      <section
        className="flex flex-1 flex-col items-center justify-center bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: 'url("/background.svg")' }}
      >
        <div className="w-full max-w-2xl rounded-lg px-4 py-3 sm:px-8 sm:py-4">
          <div className="flex flex-1 items-center justify-center px-3 sm:px-5">
            <div className="w-full max-w-2xl">
              <div className="space-y-4 rounded-2xl bg-white/95 p-5 shadow-xl sm:p-6 md:p-7">
                {status === 'loading' && (
                  <>
                    <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
                      Verifying your email
                    </h1>
                    <p className="text-sm leading-snug text-gray-600 sm:text-base">
                      We are completing your email verification. Please wait a moment.
                    </p>
                    <AuthContactFooter compact />
                  </>
                )}

                {status === 'success' && (
                  <>
                    <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
                      Email verified
                    </h1>
                    <p className="text-sm leading-snug text-gray-600 sm:text-base">
                      Your email is verified. Please log in to continue.
                    </p>
                    <Link
                      to={loginHref}
                      className={clsx(
                        authTremorPrimarySubmitClass,
                        '!flex items-center justify-center !py-2.5 no-underline',
                      )}
                    >
                      Go to login
                    </Link>
                    <AuthContactFooter />
                  </>
                )}

                {status === 'missing_code' && (
                  <>
                    <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
                      Invalid verification link
                    </h1>
                    <p className="text-sm leading-snug text-gray-600 sm:text-base">
                      Verification parameters are missing. Request a new verification
                      email.
                    </p>
                    <Link
                      to={loginHref}
                      className={clsx(
                        authTremorPrimarySubmitClass,
                        '!flex items-center justify-center !py-2.5 no-underline',
                      )}
                    >
                      Go to login
                    </Link>
                    <AuthContactFooter />
                  </>
                )}

                {status === 'error' && (
                  <>
                    <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
                      Could not verify your email
                    </h1>
                    <p className="text-sm leading-snug text-gray-600 sm:text-base">
                      This verification link is invalid or expired. Request a new one from
                      your account.
                    </p>
                    <Link
                      to={loginHref}
                      className={clsx(
                        authTremorPrimarySubmitClass,
                        '!flex items-center justify-center !py-2.5 no-underline',
                      )}
                    >
                      Go to login
                    </Link>
                    <AuthContactFooter />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
